import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./state";
import { ErrorBoundary } from "./components/error-boundary";
import { get } from "./api/client";
import type { NinjaAccessStatus } from "./types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginPage } from "./pages/login";
import { DashboardPage } from "./pages/dashboard";
import { ShopPage } from "./pages/shop";
import { LeaderboardPage } from "./pages/leaderboard";

// Legacy hash URLs (old bookmarks) still arrive as e.g. /#/dashboard —
// rewrite them to real paths before routing.
if (window.location.hash.startsWith("#/")) {
  window.history.replaceState(null, "", window.location.hash.slice(1));
}

/** Guards the student pages: without a session, send the ninja back to login. */
function RequireLogin() {
  const { isLoggedIn, isLocked, setLocked, facilityId, studentId } = useAuth();
  const location = useLocation();
  const [checkingLock, setCheckingLock] = useState(true);

  useEffect(() => {
    if (!isLoggedIn || !studentId || !facilityId) {
      setCheckingLock(false);
      return;
    }
    let cancelled = false;
    setCheckingLock(true);
    get<NinjaAccessStatus>(
      `/facilities/${facilityId}/ninjas/${studentId}/access-status`,
    ).then((response) => {
      if (cancelled) return;
      if (response.data) setLocked(response.data.locked);
      setCheckingLock(false);
    });
    return () => {
      cancelled = true;
    };
  }, [facilityId, isLoggedIn, location.pathname, setLocked, studentId]);

  if (!isLoggedIn) return <Navigate to="/" replace />;
  if (isLocked) return <StudentLockScreen />;
  if (checkingLock) return <main className="lock-screen"><p>Checking account access…</p></main>;
  return <Outlet />;
}

function StudentLockScreen() {
  const { t } = useTranslation();
  const { facilityId, studentId, setLocked, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  async function checkAgain() {
    if (!studentId) return;
    setChecking(true);
    setError("");
    const response = await get<NinjaAccessStatus>(
      `/facilities/${facilityId}/ninjas/${studentId}/access-status`,
    );
    setChecking(false);
    if (response.data && !response.data.locked) {
      setLocked(false);
      navigate("/dashboard", { replace: true });
    } else if (response.error) {
      setError(response.error);
    }
  }

  return (
    <main className="lock-screen">
      <section className="lock-card" role="status" aria-live="polite">
        <div className="lock-icon" aria-hidden="true">🔒</div>
        <h1>{t("lock.title")}</h1>
        <p>{t("lock.message")}</p>
        <p className="lock-help">{t("lock.help")}</p>
        {error && <p className="lock-error" role="alert">{error}</p>}
        <div className="lock-actions">
          <button className="btn btn-green" onClick={() => void checkAgain()} disabled={checking}>
            {checking ? t("lock.checking") : t("lock.checkAgain")}
          </button>
          <button className="btn btn-secondary" onClick={logout}>{t("nav.logout")}</button>
        </div>
      </section>
    </main>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route element={<RequireLogin />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
